import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import LoginMode from '../../../Types/LoginMode';
import { setLoginMode } from '../../../Actions/Login.action';
import { translate } from '../../../Services/translation';
import Input from '../../Inputs/Input';
import Dropdown, { ListBoxOption } from '../../Dropdown';
import Checkbox from '../../Inputs/Checkbox';
import { post } from '../../../Services/http';
import { triggerToast } from '../../../Services/Toast.service';
import LoadingSpinner from '../../LoadingSpinner';
import Icon from '../../Icon';
import { useTypedSelector } from '../../../Hooks/useTypedSelector';
import { camelCase } from 'lodash-es';
import { ValidationError } from 'yup';
import { registerUserValidationSchema } from '../misc/RegisterUserValidationSchema';
import Listbox from '../../Listbox';

const LoginRegisterAccount = () => {
  const dispatch = useDispatch();
  const { country } = useTypedSelector((state) => state.login);

  const [registerAccountFormData, setRegisterAccountFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    country,
    phone: '',
    firstName: '',
    lastName: '',
    address: '',
    careOf: '',
    zipCode: '',
    city: '',
    allowMarketing: false,
  });
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string | undefined;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [languePickerOpen, setLanguePickerOpen] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setRegisterAccountFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmitError = async (response: Response) => {
    const data = (await response.json()) as { [key: string]: string };

    // Keys from server is not in camelCase, convert them
    const newErrors = {};
    for (let key of Object.keys(data)) {
      newErrors[camelCase(key)] = data[key];
    }
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      ...newErrors,
    }));
  };

  const selectedCountryOption = global.LitiumConstants.countries.find(
    (c) => c.value == registerAccountFormData.country
  );

  const selectedListOption = selectedCountryOption && {
    label: selectedCountryOption.text,
    value: selectedCountryOption.value,
  };

  const languageOptionList = useMemo(
    () =>
      global.LitiumConstants.countries.map((c) => ({
        label: c.text,
        value: c.value,
        selected: c.value == registerAccountFormData.country,
        disabled: c.value == registerAccountFormData.country,
      })),
    [registerAccountFormData.country]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      await registerUserValidationSchema.validate(
        registerAccountFormData,
        {
          abortEarly: false,
        }
      );

      const res = await post(
        '/api/userinfo/register',
        registerAccountFormData
      );
      if (res.ok) {
        location.href = await res.text();
        setIsSuccess(true);
      } else {
        setIsLoading(false);
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        const validationErr = err as ValidationError;
        const newValidationErrors = validationErr.inner.reduce(
          (prev, e) => ({ ...prev, ['' + e.path]: e.message }),
          {}
        );
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          ...newValidationErrors,
        }));
      } else if (err.response) {
        if (err.response.status != 500) {
          handleSubmitError(err.response);
        } else {
          const errorMessage = await err.response.text();
          triggerToast({
            title: translate('general.error'),
            text: errorMessage,
            type: 'error',
            autoClose: false,
          });
        }
      }
      setIsLoading(false);
    }
  };

  const validateOnBlur = async (key: string, value: string) => {
    try {
      await registerUserValidationSchema.validateAt(
        key,
        { ...registerAccountFormData, [key]: value },
        { abortEarly: true }
      );

      setValidationErrors((existingErrors) => ({
        ...existingErrors,
        [key]: undefined,
      }));
    } catch (err) {
      setValidationErrors((existingErrors) => ({
        ...existingErrors,
        [key]: err.message,
      }));
    }
  };

  const getValidationErrorLabel = (key: string) => {
    const errMsg = validationErrors[key];
    return !!errMsg ? errMsg : undefined;
  };

  return (
    <>
      <h1 className="mb-6">{translate('login.create-new-account')}</h1>
      <form className="grid grid-cols-2 gap-4" onSubmit={handleSubmit}>
        <h4 className="col-span-2 ">
          {translate('login.email-and-password')}
        </h4>
        <Input
          inputSize="xlarge"
          wrapperClassName="col-span-2"
          name="username"
          showAnimatedLabel={true}
          label={translate('general.email')}
          value={registerAccountFormData.username}
          type="email"
          onChange={handleChange}
          onBlur={(e) => validateOnBlur(e.target.name, e.target.value)}
          message={getValidationErrorLabel('username')}
          inputState={!!validationErrors['username'] ? 'error' : 'default'}
          disabled={isLoading}
          required
        />
        <Input
          inputSize="xlarge"
          wrapperClassName="col-span-2"
          name="password"
          showAnimatedLabel={true}
          label={translate('general.password')}
          value={registerAccountFormData.password}
          onChange={handleChange}
          onBlur={(e) => validateOnBlur(e.target.name, e.target.value)}
          message={getValidationErrorLabel('password')}
          inputState={!!validationErrors['password'] ? 'error' : 'default'}
          type="password"
          disabled={isLoading}
          required
        />
        <Input
          inputSize="xlarge"
          wrapperClassName="col-span-2"
          name="confirmPassword"
          showAnimatedLabel={true}
          label={translate('general.confirm-password')}
          value={registerAccountFormData.confirmPassword}
          onChange={handleChange}
          onBlur={(e) => validateOnBlur(e.target.name, e.target.value)}
          message={getValidationErrorLabel('confirmPassword')}
          inputState={
            !!validationErrors['confirmPassword'] ? 'error' : 'default'
          }
          type="password"
          disabled={isLoading}
          required
        />
        <h4 className="col-span-2 mt-6">
          {translate('login.phone-and-adress')}
        </h4>
        <Dropdown
          className="col-span-2"
          size="large"
          label={registerAccountFormData.country}
          onChange={({ value }) => {
            setRegisterAccountFormData((prevFormData) => ({
              ...prevFormData,
              country: value,
            }));
          }}
          options={global.LitiumConstants.countries.map((c) => ({
            label: c.text,
            value: c.value,
          }))}
          selectedValue={registerAccountFormData.country}
          disabled={isLoading}
          hideForDesktop={true}
        />
        <Listbox
          optionList={languageOptionList}
          setSelectedOption={(e) =>
            setRegisterAccountFormData((prevData) => ({
              ...prevData,
              country: e.value,
            }))
          }
          selectedOption={selectedListOption}
          inputSize="large"
          isOpen={languePickerOpen}
          setIsOpen={setLanguePickerOpen}
          label={translate('language-select.country')}
          continerClassName="col-span-2 lg-block hidden"
          showStickyLabel={true}
        />
        <Input
          inputSize="xlarge"
          wrapperClassName="col-span-2"
          name="phone"
          showAnimatedLabel={true}
          label={translate('general.phone')}
          value={registerAccountFormData.phone}
          onChange={handleChange}
          onBlur={(e) => validateOnBlur(e.target.name, e.target.value)}
          message={getValidationErrorLabel('phone')}
          inputState={!!validationErrors['phone'] ? 'error' : 'default'}
          disabled={isLoading}
          required
        />
        <Input
          inputSize="xlarge"
          wrapperClassName=""
          name="firstName"
          showAnimatedLabel={true}
          label={translate('general.firstname')}
          value={registerAccountFormData.firstName}
          onChange={handleChange}
          onBlur={(e) => validateOnBlur(e.target.name, e.target.value)}
          message={getValidationErrorLabel('firstName')}
          inputState={
            !!validationErrors['firstName'] ? 'error' : 'default'
          }
          disabled={isLoading}
          required
        />
        <Input
          inputSize="xlarge"
          wrapperClassName=""
          name="lastName"
          label={translate('general.lastname')}
          showAnimatedLabel={true}
          value={registerAccountFormData.lastName}
          onChange={handleChange}
          onBlur={(e) => validateOnBlur(e.target.name, e.target.value)}
          message={getValidationErrorLabel('lastName')}
          inputState={!!validationErrors['lastName'] ? 'error' : 'default'}
          required
        />
        <Input
          inputSize="xlarge"
          wrapperClassName="col-span-2"
          name="address"
          showAnimatedLabel={true}
          label={translate('general.address')}
          value={registerAccountFormData.address}
          onChange={handleChange}
          onBlur={(e) => validateOnBlur(e.target.name, e.target.value)}
          message={getValidationErrorLabel('address')}
          inputState={!!validationErrors['address'] ? 'error' : 'default'}
          disabled={isLoading}
          required
        />
        <Input
          inputSize="xlarge"
          wrapperClassName="col-span-2"
          name="careOf"
          showAnimatedLabel={true}
          label={translate('general.care-of')}
          value={registerAccountFormData.careOf}
          onChange={handleChange}
          disabled={isLoading}
        />
        <Input
          inputSize="xlarge"
          wrapperClassName=""
          name="zipCode"
          label={translate('general.zip-code')}
          showAnimatedLabel={true}
          value={registerAccountFormData.zipCode}
          onChange={handleChange}
          onBlur={(e) => validateOnBlur(e.target.name, e.target.value)}
          message={getValidationErrorLabel('zipCode')}
          inputState={!!validationErrors['zipCode'] ? 'error' : 'default'}
          disabled={isLoading}
          required
        />
        <Input
          inputSize="xlarge"
          wrapperClassName=""
          name="city"
          showAnimatedLabel={true}
          label={translate('general.city')}
          value={registerAccountFormData.city}
          onChange={handleChange}
          onBlur={(e) => validateOnBlur(e.target.name, e.target.value)}
          message={getValidationErrorLabel('city')}
          inputState={!!validationErrors['city'] ? 'error' : 'default'}
          disabled={isLoading}
          required
        />
        <Checkbox
          onChange={(e) =>
            setRegisterAccountFormData((prevData) => ({
              ...prevData,
              allowMarketing: e.target.checked,
            }))
          }
          label={<>{translate('login.sign-up-for-newsletter')} </>}
          checked={registerAccountFormData.allowMarketing}
          name="allowMarketing"
          disabled={isLoading}
          wrapperClassName="col-span-2"
        />
        <button
          className="btn btn--xlarge btn--fullwidth btn--black col-span-2"
          type="submit"
          disabled={isLoading || isSuccess}
        >
          {!isLoading && translate('login.create-new-account')}
          {isLoading && !isSuccess && <LoadingSpinner />}
          {isSuccess && <Icon name="check" />}
        </button>
      </form>
      <div className={clsx('flex', 'flex-col', 'mt-6')}>
        <button
          onClick={() => dispatch(setLoginMode(LoginMode.Login))}
          className="underlined-link col-span-2 mx-auto self-start text-sm uppercase after:w-full"
        >
          {translate('login.already-customer')}
        </button>
      </div>
    </>
  );
};

export default LoginRegisterAccount;
